(() => {
  if (window.__macGradedFiltersLoaded) return;
  window.__macGradedFiltersLoaded = true;

  const PARAMS = { grade: 'mac_grade', company: 'mac_company', type: 'mac_ctype' };
  const STATE_PARAMS = [...Object.values(PARAMS), 'mac_page'];
  const PREVIEW_PARAMS = ['preview_theme_id', '_fd', 'pb'];
  let activeRequest = null;

  const ready = (callback) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  };

  const readValues = (param) => {
    const raw = new URL(window.location.href).searchParams.get(param);
    return raw ? raw.split(',').map((value) => value.trim()).filter(Boolean) : [];
  };

  const quote = (value) =>
    String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');

  const titleTerm = (value) => `title:"${quote(value)}"`;
  const tagTerm = (value) => `tag:"${quote(value)}"`;
  const orClause = (values) => values.length ? `(${values.join(' OR ')})` : '';

  const GRADES = ['10', '9.5', '9', '8.5', '8', '7.5', '7', '6.5', '6', '5.5', '5', '4.5', '4', '3.5', '3', '2.5', '2', '1.5', '1'];

  const graderGradeTerms = (grader, grade) => {
    const values = [];

    if (grader === 'BGS') {
      values.push(titleTerm(`BGS ${grade}`));
      values.push(titleTerm(`Beckett ${grade}`));
    } else {
      values.push(titleTerm(`${grader} ${grade}`));
    }

    return values;
  };

  const graderClause = (grader) => {
    const values = [];

    GRADES.forEach((grade) => {
      values.push(...graderGradeTerms(grader, grade));
    });

    return orClause(values);
  };

  const companyClause = (company) => {
    switch (company) {
      case 'PSA':
        return graderClause('PSA');
      case 'BGS':
        return graderClause('BGS');
      case 'CGC':
        return graderClause('CGC');
      case 'SGC':
        return graderClause('SGC');
      case 'TAG':
        return graderClause('TAG');
      default:
        return '';
    }
  };

  const gradeClause = (grade) => {
    return orClause([
      ...graderGradeTerms('PSA', grade),
      ...graderGradeTerms('BGS', grade),
      ...graderGradeTerms('CGC', grade),
      ...graderGradeTerms('SGC', grade),
      ...graderGradeTerms('TAG', grade)
    ]);
  };

  const allGradedClause = () => {
    return orClause([
      graderClause('PSA'),
      graderClause('BGS'),
      graderClause('CGC'),
      graderClause('SGC'),
      graderClause('TAG')
    ]);
  };

  const typeClause = (type) => {
    switch (type) {
      case 'baseball':
        return tagTerm('baseball');
      case 'football':
        return tagTerm('football');
      case 'basketball':
        return tagTerm('basketball');
      case 'more-sports':
        return orClause([
          'hockey',
          'soccer',
          'golf',
          'tennis',
          'nascar',
          'racing',
          'wrestling',
          'wwe',
          'ufc',
          'mma',
          'college',
          'ncaa'
        ].map(tagTerm));
      case 'pokemon':
        return orClause([
          'pokemon',
          'pokemon-cards',
          'pokemon-english',
          'pokemon-japanese'
        ].map(tagTerm));
      case 'more-tcg':
        return orClause([
          'tcg',
          'more-tcg',
          'yugioh',
          'yu-gi-oh',
          'magic-the-gathering',
          'mtg',
          'one-piece',
          'lorcana',
          'dragon-ball',
          'digimon'
        ].map(tagTerm));
      default:
        return '';
    }
  };

  const collectSelections = (sidebar) => {
    const groups = { grade: [], company: [], type: [] };

    sidebar.querySelectorAll('[data-mac-graded-option]:checked').forEach((input) => {
      const group = input.dataset.macGradedGroup;
      if (groups[group]) groups[group].push(input.value);
    });

    return groups;
  };

  const buildQuery = (groups) => {
    const clauses = [];

    // CRITICAL:
    // Every filtered result must match a recognized grader + numeric grade.
    // This prevents raw singles from ever entering Graded Cards results.
    clauses.push(allGradedClause());

    const grades = groups.grade.map(gradeClause).filter(Boolean);
    const companies = groups.company.map(companyClause).filter(Boolean);
    const types = groups.type.map(typeClause).filter(Boolean);

    if (grades.length) clauses.push(orClause(grades));
    if (companies.length) clauses.push(orClause(companies));
    if (types.length) clauses.push(orClause(types));

    return clauses.join(' AND ');
  };

  const setStateUrl = (sidebar, resetPage = true) => {
    const groups = collectSelections(sidebar);
    const url = new URL(window.location.href);

    Object.entries(PARAMS).forEach(([group, param]) => {
      if (groups[group].length) {
        url.searchParams.set(param, groups[group].join(','));
      } else {
        url.searchParams.delete(param);
      }
    });

    if (resetPage) url.searchParams.delete('mac_page');

    window.history.replaceState(
      { macGradedFilters: true },
      '',
      url.toString()
    );

    return groups;
  };

  const buildFetchUrl = (groups) => {
    const stateUrl = new URL(window.location.href);
    const hasFilters = Object.values(groups).some((values) => values.length > 0);

    if (!hasFilters) {
      const clean = new URL(stateUrl.toString());
      STATE_PARAMS.forEach((param) => clean.searchParams.delete(param));
      clean.searchParams.delete('page');
      return clean;
    }

    const searchUrl = new URL('/search', window.location.origin);
    searchUrl.searchParams.set('view', 'mac-filter');
    searchUrl.searchParams.set('type', 'product');
    searchUrl.searchParams.set('q', buildQuery(groups));

    const sortBy = stateUrl.searchParams.get('sort_by');
    if (sortBy) searchUrl.searchParams.set('sort_by', sortBy);

    const page = stateUrl.searchParams.get('mac_page');
    if (page && page !== '1') {
      searchUrl.searchParams.set('page', page);
    }

    PREVIEW_PARAMS.forEach((param) => {
      if (stateUrl.searchParams.has(param)) {
        searchUrl.searchParams.set(param, stateUrl.searchParams.get(param));
      }
    });

    return searchUrl;
  };

  const looksLikeActualGradedCard = (card) => {
    const title =
      card.querySelector(
        '.product-card__title, [class*="product-title"], a[aria-label]'
      )?.textContent ||
      card.querySelector('a[aria-label]')?.getAttribute('aria-label') ||
      '';

    const normalized = title.replace(/\s+/g, ' ').trim();

    const graderPattern =
      /\b(?:PSA|CGC|SGC|TAG)\s+(?:10|9\.5|9|8\.5|8|7\.5|7|6\.5|6|5\.5|5|4\.5|4|3\.5|3|2\.5|2|1\.5|1)\b/i;

    const beckettPattern =
      /\b(?:BGS|Beckett)\s+(?:10|9\.5|9|8\.5|8|7\.5|7|6\.5|6|5\.5|5|4\.5|4|3\.5|3|2\.5|2|1\.5|1)\b/i;

    return graderPattern.test(normalized) || beckettPattern.test(normalized);
  };

  const removeRawCardsFromResults = () => {
    const results = document.getElementById('ResultsList');
    if (!results) return;

    const cards = results.querySelectorAll(
      'product-card, .product-card, li:has(.product-card)'
    );

    cards.forEach((card) => {
      const actualCard = card.matches('product-card, .product-card')
        ? card
        : card.querySelector('product-card, .product-card');

      if (!actualCard) return;

      if (!looksLikeActualGradedCard(actualCard)) {
        card.remove();
      }
    });
  };

  const replaceResults = (doc, originalGridClass) => {
    const currentResults = document.getElementById('ResultsList');
    const incomingResults = doc.getElementById('ResultsList');

    if (!currentResults || !incomingResults) {
      throw new Error('Product results could not be loaded.');
    }

    const currentInner = currentResults.querySelector(':scope > div');
    const incomingInner = incomingResults.querySelector(':scope > div');

    if (!currentInner || !incomingInner) {
      throw new Error('Product grid could not be loaded.');
    }

    const incomingGrid = incomingInner.querySelector('.product-grid');

    if (incomingGrid && originalGridClass) {
      incomingGrid.className = originalGridClass;
    }

    currentInner.innerHTML = incomingInner.innerHTML;

    // Second safety net:
    // remove anything that doesn't visibly contain grader + grade.
    removeRawCardsFromResults();
  };

  const updateStyles = (sidebar) => {
    sidebar.querySelectorAll('[data-mac-graded-item]').forEach((item) => {
      const input = item.querySelector('[data-mac-graded-option]');
      item.classList.toggle('is-active', Boolean(input?.checked));
    });

    const clear = sidebar.querySelector('[data-mac-graded-clear]');

    if (clear) {
      clear.hidden =
        sidebar.querySelectorAll('[data-mac-graded-option]:checked').length === 0;
    }
  };

  const syncFromUrl = (sidebar) => {
    Object.entries(PARAMS).forEach(([group, param]) => {
      const selected = new Set(readValues(param));

      sidebar
        .querySelectorAll(
          `[data-mac-graded-option][data-mac-graded-group="${group}"]`
        )
        .forEach((input) => {
          input.checked = selected.has(input.value);
        });
    });

    updateStyles(sidebar);
  };

  const loadResults = async (sidebar, groups, originalGridClass) => {
    if (activeRequest) activeRequest.abort();

    activeRequest = new AbortController();

    const layout = sidebar.closest('.mac-graded-layout');
    const results = document.getElementById('ResultsList');

    layout?.classList.add('mac-filters-loading');
    results?.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(buildFetchUrl(groups).toString(), {
        signal: activeRequest.signal,
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        throw new Error(`Filter request failed (${response.status}).`);
      }

      const doc = new DOMParser().parseFromString(
        await response.text(),
        'text/html'
      );

      replaceResults(doc, originalGridClass);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('[MAC graded filters]', error);
      }
    } finally {
      if (!activeRequest?.signal.aborted) {
        layout?.classList.remove('mac-filters-loading');
        document
          .getElementById('ResultsList')
          ?.removeAttribute('aria-busy');
      }
    }
  };

  const init = () => {
    if (!window.location.pathname.includes('/collections/graded-cards')) return;

    const template = document.getElementById('MacGradedSidebarTemplate');
    const results = document.getElementById('ResultsList');

    if (
      !template ||
      !results ||
      document.querySelector('.mac-graded-sidebar')
    ) {
      return;
    }

    const wrapper = results.parentElement;
    if (!wrapper) return;

    wrapper.classList.add('mac-graded-wrapper');

    const layout = document.createElement('div');
    layout.className = 'mac-graded-layout';

    wrapper.insertBefore(layout, results);

    const fragment = template.content.cloneNode(true);
    const sidebar = fragment.querySelector('.mac-graded-sidebar');

    if (!sidebar) return;

    layout.append(sidebar, results);
    template.remove();

    const originalGridClass =
      document.querySelector('#ResultsList .product-grid')?.className || '';

    syncFromUrl(sidebar);

    sidebar.addEventListener('change', (event) => {
      if (!event.target.matches('[data-mac-graded-option]')) return;

      const groups = setStateUrl(sidebar, true);
      updateStyles(sidebar);
      loadResults(sidebar, groups, originalGridClass);
    });

    sidebar
      .querySelector('[data-mac-graded-clear]')
      ?.addEventListener('click', () => {
        sidebar
          .querySelectorAll('[data-mac-graded-option]')
          .forEach((input) => {
            input.checked = false;
          });

        const groups = setStateUrl(sidebar, true);
        updateStyles(sidebar);
        loadResults(sidebar, groups, originalGridClass);
      });

    document.addEventListener('click', (event) => {
      const link = event.target.closest('#ResultsList a[href]');
      if (!link) return;

      const url = new URL(link.href, window.location.origin);

      if (
        !url.pathname.includes('/search') ||
        !url.searchParams.has('page')
      ) {
        return;
      }

      event.preventDefault();

      const stateUrl = new URL(window.location.href);
      stateUrl.searchParams.set('mac_page', url.searchParams.get('page'));

      window.history.replaceState(
        { macGradedFilters: true },
        '',
        stateUrl.toString()
      );

      loadResults(
        sidebar,
        collectSelections(sidebar),
        originalGridClass
      );

      document
        .getElementById('ResultsList')
        ?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
    });

    window.addEventListener('popstate', () => {
      syncFromUrl(sidebar);

      loadResults(
        sidebar,
        collectSelections(sidebar),
        originalGridClass
      );
    });

    const initialGroups = collectSelections(sidebar);

    if (
      Object.values(initialGroups).some(
        (values) => values.length > 0
      )
    ) {
      loadResults(
        sidebar,
        initialGroups,
        originalGridClass
      );
    }
  };

  ready(init);
})();